import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from 'recharts'
import {
	getCategoryTrendData,
	CATEGORY_COLORS,
	CATEGORY_LABELS,
	formatConversionRate,
} from '../../utils/formatConversionData'

import type { CategoryConversionMetrics } from '../../types'

interface ConversionTrendChartProps {
	metrics: CategoryConversionMetrics | undefined
	isLoading?: boolean
}

export default function ConversionTrendChart({
	metrics,
	isLoading,
}: ConversionTrendChartProps) {
	const chartData = getCategoryTrendData(metrics)

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>อัตรา Conversion ตามช่วงเวลา</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 animate-pulse rounded bg-muted" />
				</CardContent>
			</Card>
		)
	}

	if (!metrics || chartData.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>อัตรา Conversion ตามช่วงเวลา</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex h-64 items-center justify-center">
						<p className="text-muted-foreground">ไม่มีข้อมูล</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>อัตรา Conversion ตามช่วงเวลา</CardTitle>
				<p className="text-sm text-muted-foreground">
					เปรียบเทียบอัตราการฝากของแต่ละกลุ่มผู้รับ เช่น 1 วัน = ฝากภายใน 1
					วันหลังรับข้อความ
				</p>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<LineChart
						data={chartData}
						margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
					>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--border))"
							opacity={0.5}
						/>
						<XAxis
							dataKey="period"
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
						/>
						<YAxis
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
							tickFormatter={(value) => `${value.toFixed(0)}%`}
							domain={[0, 'auto']}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: 'hsl(var(--card))',
								border: '1px solid hsl(var(--border))',
								borderRadius: '8px',
								boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
							}}
							formatter={(value: number, name: string) => {
								const label =
									CATEGORY_LABELS[name as keyof typeof CATEGORY_LABELS] || name
								return [formatConversionRate(value), label]
							}}
						/>
						<Legend
							wrapperStyle={{ paddingTop: '10px' }}
							formatter={(value: string) =>
								CATEGORY_LABELS[value as keyof typeof CATEGORY_LABELS] || value
							}
						/>
						<Line
							type="monotone"
							dataKey="newDepositor"
							name="newDepositor"
							stroke={CATEGORY_COLORS.newDepositor}
							strokeWidth={2}
							dot={{ fill: CATEGORY_COLORS.newDepositor, r: 4 }}
							activeDot={{ r: 6 }}
						/>
						<Line
							type="monotone"
							dataKey="inactive7d"
							name="inactive7d"
							stroke={CATEGORY_COLORS.inactive7d}
							strokeWidth={2}
							dot={{ fill: CATEGORY_COLORS.inactive7d, r: 4 }}
							activeDot={{ r: 6 }}
						/>
						<Line
							type="monotone"
							dataKey="inactive14d"
							name="inactive14d"
							stroke={CATEGORY_COLORS.inactive14d}
							strokeWidth={2}
							dot={{ fill: CATEGORY_COLORS.inactive14d, r: 4 }}
							activeDot={{ r: 6 }}
						/>
						<Line
							type="monotone"
							dataKey="inactive30d"
							name="inactive30d"
							stroke={CATEGORY_COLORS.inactive30d}
							strokeWidth={2}
							dot={{ fill: CATEGORY_COLORS.inactive30d, r: 4 }}
							activeDot={{ r: 6 }}
						/>
						<Line
							type="monotone"
							dataKey="regularUser"
							name="regularUser"
							stroke={CATEGORY_COLORS.regularUser}
							strokeWidth={2}
							dot={{ fill: CATEGORY_COLORS.regularUser, r: 4 }}
							activeDot={{ r: 6 }}
						/>
					</LineChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	)
}
